
import React, { useEffect, useRef } from 'react';
import { getContactConfig } from '@/config/contactConfig';
import { Navbar } from '@/components/homepage/Navbar';
import Footer from '@/components/homepage/Footer';
import { useLanguage } from '@/context/LanguageContext';
import WhatsAppIcon from '@/components/homepage/icons/WhatsAppIcon';
import { Mail } from 'lucide-react';


const ContactUs = () => {
  const { t } = useLanguage();
  const pageTopRef = useRef<HTMLDivElement>(null);
  const contactConfig = getContactConfig();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // WhatsApp contact item component
  const WhatsAppContact = ({
    languageKey,
    countryCode,
    phoneNumber
  }: {
    languageKey: string;
    countryCode: string;
    phoneNumber: string
  }) => {
    const whatsappLink = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}`;

    return (
      <div className="flex items-center p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg mb-2 hover:bg-white/10 transition-all">
        <div className="w-8 h-8 flex items-center justify-center mr-3">
          <span className="emoji-flag-language">{countryCode}</span>
        </div>
        <span className="text-base text-white flex-grow">{t(languageKey)}</span>
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-8 h-8 bg-purple-500/20 hover:bg-purple-500/30 rounded-full transition-colors"
        >
          <WhatsAppIcon className="h-5 w-5 text-white" />
        </a>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div ref={pageTopRef} className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900">
        {/* Background elements */}
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl"></div>

        <div className="relative z-10 container mx-auto px-4 pt-28 pb-16">
          {/* Title and subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-4xl font-montserrat font-bold text-white mb-2">
              {t('contactUs.title')}
            </h1>
            <p className="text-2xl md:text-3xl text-gray-200 mt-8">
              {t('contactUs.subtitle')}
            </p>
          </div>

          {/* Main content */}
          <div className="max-w-2xl mx-auto glassmorphism-dark p-8 rounded-xl">
            {contactConfig.showEmail && contactConfig.emailAddress && (
              <>
                <h2 className="text-2xl font-semibold text-white mb-6">
                  {t('contactUs.email.title')}
                </h2>

                <div className="flex items-center p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg mb-8 hover:bg-white/10 transition-all">
                  <div className="w-8 h-8 flex items-center justify-center mr-3">
                    <Mail className="h-5 w-5 text-purple-400" />
                  </div>
                  <span className="text-base text-white flex-grow">{contactConfig.emailAddress}</span>
                  <a
                    href={`mailto:${contactConfig.emailAddress}`}
                    className="inline-flex items-center justify-center w-8 h-8 bg-purple-500/20 hover:bg-purple-500/30 rounded-full transition-colors"
                  >
                    <Mail className="h-4 w-4 text-white" />
                  </a>
                </div>

                <div className="border-t border-white/10 my-8"></div>
              </>
            )}

            <h2 className="text-2xl font-semibold text-white mb-6">
              {t('contactUs.whatsapp.title')}
            </h2>

            <div className="space-y-2 mb-8">
              {contactConfig.contacts.map((contact, idx) => (
                <div key={idx} className={contact.visible ? '' : 'hidden'}>
                  <WhatsAppContact
                    languageKey={contact.languageKey}
                    countryCode={contact.countryCode}
                    phoneNumber={contact.phoneNumber}
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-purple-600/10 rounded-lg border border-purple-500/30">
              <p className="text-gray-300 text-sm">
                {t('contactUs.whatsapp.urgent')}
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ContactUs;
